import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import LuminartLogo from "../components/LuminartLogo";
import ProjectCard from "../components/ProjectCard";
import WorkLightbox from "../components/WorkLightbox";
import Footer from "../components/Footer";
import { projectsForService } from "../data/services";
import { useContent } from "../lib/content";

const ease = [0.16, 1, 0.3, 1];

export const ServicePage = () => {
  const { slug } = useParams();
  const { services, projects: allProjects, loaded } = useContent();
  const service = services.find((s) => s.slug === slug);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!loaded) {
    return <div className="min-h-screen bg-[#0a0a0c]" data-testid="service-page-loading" />;
  }
  if (!service) return <Navigate to="/" replace />;

  const projects = projectsForService(service, allProjects);

  return (
    <div className="min-h-screen bg-[#0a0a0c]" data-testid="service-page">
      {/* Slim header — main navbar stays exclusive to the home page */}
      <header className="sticky top-0 z-40 flex items-center justify-between px-6 md:px-12 lg:px-16 h-20 border-b border-white/10 bg-[#0a0a0c]/85 backdrop-blur-md">
        <Link
          to="/"
          data-testid="service-home-link"
          className="flex items-center gap-3 group"
        >
          <LuminartLogo className="w-9 h-auto" glow={false} />
          <span className="font-heading text-sm tracking-[0.35em] uppercase text-white/90 group-hover:text-white transition-colors hidden sm:block">
            Luminart
          </span>
        </Link>
        <Link
          to="/?section=services"
          data-testid="service-back-link"
          className="group inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-white/50 hover:text-white transition-colors"
        >
          <ArrowLeft
            size={13}
            className="group-hover:-translate-x-1 transition-transform duration-300"
          />
          Back
        </Link>
      </header>

      <main className="px-6 md:px-12 lg:px-16 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease }}
        >
          <span className="text-[11px] uppercase tracking-[0.3em] text-white/85">
            What We Do
          </span>
          <div className="mt-2 h-px w-9 bg-[#c8b99d]" />
          <h1
            className="mt-8 font-heading text-4xl sm:text-5xl lg:text-6xl font-extralight tracking-tight text-white"
            data-testid="service-title"
          >
            {service.title}
          </h1>
          <p className="mt-6 max-w-xl text-base md:text-lg font-light leading-relaxed text-white/50">
            {service.intro}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.15, ease }}
          className="mt-12 w-fit mx-auto border border-white/10 bg-black"
        >
          <img
            src={service.img}
            alt={service.title}
            className="max-h-[70vh] w-auto max-w-full object-contain"
            data-testid="service-hero-image"
          />
        </motion.div>

        {/* Projects in this service */}
        <div className="mt-20 md:mt-24">
          <div className="flex items-end justify-between gap-8">
            <div>
              <span className="text-[11px] uppercase tracking-[0.3em] text-white/85">
                Selected Projects
              </span>
              <div className="mt-2 h-px w-9 bg-[#c8b99d]" />
            </div>
            <span className="text-[10px] uppercase tracking-[0.3em] text-white/30 pb-1">
              ({String(projects.length).padStart(2, "0")})
            </span>
          </div>

          {projects.length > 0 ? (
            <div
              className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5"
              data-testid="service-gallery"
            >
              {projects.map((p) => (
                <motion.div
                  key={p.title}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.9, ease }}
                >
                  <ProjectCard
                    p={p}
                    i={0}
                    onOpen={() => setOpen(p)}
                    className="w-full"
                  />
                </motion.div>
              ))}
            </div>
          ) : (
            <p
              className="mt-10 max-w-md text-sm font-light leading-relaxed text-white/40"
              data-testid="service-gallery-empty"
            >
              Case studies for this service are currently in production — get
              in touch to see recent work in this space.
            </p>
          )}
        </div>

        {/* CTA */}
        <div className="mt-24 border-y border-white/10 py-14 md:py-16 flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <span className="text-[11px] uppercase tracking-[0.3em] text-white/85">
              Have a Project in Mind?
            </span>
            <div className="mt-2 h-px w-9 bg-[#c8b99d]" />
            <p className="mt-5 max-w-md text-sm font-light leading-relaxed text-white/45">
              Tell us about your project — we aim to respond within 24 hours.
            </p>
          </div>
          <Link
            to="/?section=contact"
            data-testid="service-cta-contact"
            className="group inline-flex items-center gap-3 self-start border border-white/25 px-8 py-4 text-[10px] uppercase tracking-[0.25em] text-white/80 hover:border-white hover:text-white transition-colors duration-300"
          >
            Get in Touch
            <ArrowRight
              size={13}
              className="group-hover:translate-x-1 transition-transform duration-300"
            />
          </Link>
        </div>
      </main>

      <Footer />

      <WorkLightbox
        project={open}
        index={open ? projects.indexOf(open) : 0}
        total={projects.length}
        onClose={() => setOpen(null)}
      />
    </div>
  );
};

export default ServicePage;
